import React, { useContext, useState, useEffect, useRef } from "react";
import { NewsContext } from "../../contexts/NewsContext";
import { AuthContext } from "../../contexts/AuthContext"; 
import { SubscriptionContext } from '../../contexts/SubscriptionContext';
import ThemeContext from "../../contexts/ThemeContext"; 
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import './style.css';
import darkModeLogo from '../assets/darkmode-logo.webp'; 
import Logo from '../assets/logo-jpg.webp';
import NFTAccessSection from './NFTAccessSection'; 

function Navbar({ handleModal }) {
  const { setSearchQuery } = useContext(NewsContext);
  const authContext = useContext(AuthContext); 
  const [searchInput, setSearchInput] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); 
  const { isSubscribing, setIsSubscribing } = useContext(SubscriptionContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef();
  const mobileMenuRef = useRef();
  const navigate = useNavigate();
  const links = ["Markets", "Crypto", "Web3", "Culture", "Opinion", "Politics"];
  const [scrollPosition, setScrollPosition] = useState(0); 
  const { isDarkMode } = useContext(ThemeContext);
  const [selectedNetwork, setSelectedNetwork] = useState(
    localStorage.getItem("selectedNetwork") || "mainnet"
  ); 

  const handleSearch = (e) => {
    e.preventDefault(); 
    setSearchQuery(searchInput);
    navigate(`/?q=${searchInput}`);
    setIsSearchOpen(false); 
  };

  const handleInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const toggleSearch = () => {
    setIsMobileMenuOpen(false); 
    setIsSearchOpen(!isSearchOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleClick = (content) => {
    handleModal(content);
  };

  // Update localStorage when user selects a new network
  const handleNetworkChange = (e) => {
    const newNetwork = e.target.value;
    setSelectedNetwork(newNetwork);
    localStorage.setItem("selectedNetwork", newNetwork);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!searchRef.current?.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (!dropdownRef.current?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (!mobileMenuRef.current?.contains(event.target) && !event.target.closest('.hamburger')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsSearchOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);
    };

    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
  <nav className="flex items-center border-b px-2 w-full bg-nav-bg border-nav-border max-w-screen-xl mx-auto z-1">

    <div className="flex items-center justify-between w-full lg:hidden">

      {/* button */}
      
    <button onClick={toggleMobileMenu} className="p-2 order-first bg-nav-bg hamburger">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6 text-[#3D7D63]" 
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

     
    
      {/* Add backdrop that covers the page and close the menu when clicked */}
      {isMobileMenuOpen && (
        <div onClick={toggleMobileMenu} className="fixed top-0 left-0 w-full h-full bg-black opacity-50 z-2"></div>
      )}

      {!isMobileMenuOpen && (
        <div style={{ marginLeft: '0.7rem', marginTop: '0.2rem'}} className=" order-last ">
          <ThemeToggle />
        </div>
      )}

      <div className="mx-auto md:mx-0">
        <Link to="/" className="text-xl font-bold cursor-pointer text-nav-text transition-all duration-500 hover:text-link-hover md:mx-auto">
          <img 
            className={`overflow-clip pb-1 align-middle mr-4 ${isDarkMode ? "w-36 ml-4" : "w-44"}`} 
            src={isDarkMode ? darkModeLogo : Logo} 
            alt="Logo" 
          />
        </Link>
      </div>

    </div>

    {/* Desktop Column 1 */}
    <div className="hidden-md items-center w-full">

      <div className="col-span-1 flex items-center">

          <div
            style={{ marginLeft: '0.7rem', marginTop: '0.2rem' }}
            className="order-last md:order-first"
          >
            <ThemeToggle />
          </div>
      

        {/* Logo */}

        <div className="flex-shrink-0 justify-center">
          <Link to="/"
            className="text-xl font-bold cursor-pointer text-nav-text transition-all duration-500 hover:text-link-hover"
          >
            <img
              className={`overflow-clip pb-1 align-middle ${
                isDarkMode ? 'w-36 pl-4 pb-0' : 'w-44'
              }`}
              src={isDarkMode ? darkModeLogo : Logo}
              alt="Logo"
            />
          </Link> 
        </div>
      </div>

      {/* Column 2: Links */}
      <div className="flex-1 flex justify-center col-span-2">
        <div className="flex flex-wrap justify-start md:px-0 lg:px-2 xl:px-4">
          <ul>
            {links.map((link) => (
              <li
                key={link}
                style={{ color: 'var(--text-color)' }}
                className="list-item float-left text-[0.88rem] leading-[3.13rem] pr-4 relative"
              >
                <Link
                  to={`/news/${link.toLowerCase()}`}
                  style={{ color: 'var(--text-color)' }}
                  className="cursor-pointer text-[0.75rem] font-semibold relative text-center uppercase whitespace-nowrap"
                >
                  {link}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Column 3: Actions */}
      <div className="col-span-3 flex items-center justify-end space-x-4">

       {!isSearchOpen && (
          <>
            <Link to="/subscription">
              <button 
                style={{ 
                  color: 'var(--text-color)', 
                  paddingLeft: '1rem', 
                  paddingRight: '1.4rem', 
                  height: '1.7rem', 
                  marginTop:'0rem', 
                  paddingBottom: '0.5rem', 
                  paddingTop:'0.5rem' 
                }} 
                className="rounded-tl-3xl cursor-pointer inline-block text-[0.75rem] font-semibold leading-1 uppercase bg-button-bg">
                  Subscribe
                  <i className="font-semibold" style={{fontStyle: "italic"}}></i>
              </button>
            </Link>

            {authContext.isAuthenticated ? (
              <div className="relative inline-block text-left" ref={dropdownRef}>
                <div>
                  <button type="button" aria-haspopup="true" aria-expanded={isDropdownOpen} className="font-semibold inline-flex justify-center w-full p-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none mt-2" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <span className="username-truncate"  style={{color: 'var(--text-color)'}} title={authContext.currentUser}>{authContext.currentUser.substring(0, 7)}</span>
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                  {isDropdownOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                      <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        <Link to="/account"  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 custom-link z-4" role="menuitem">Account</Link>
                        <a href="#" onClick={authContext.logout} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 custom-link" role="menuitem">Logout</a>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              <>
              <div className="flex items-center space-x-2 dropdown-container">
                <div className="flex items-center space-x-1">
      
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-4 mb-1 text-black dark:text-[#2F5E4D]"
                    fill="currentColor"
                    viewBox="0 0 24 23"
                    stroke="currentColor"
                    strokeWidth={0.5}
                  >
                    <path d="M12 2C9.2 2 7 4.2 7 7v3H6c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2h-1V7c0-2.8-2.2-5-5-5zm0 2c1.7 0 3 1.3 3 3v3H9V7c0-1.7 1.3-3 3-3zm-6 8h12v8H6v-8zm6 2c-.6 0-1 .4-1 1v2c0 .6.4 1 1 1s1-.4 1-1v-2c0-.6-.4-1-1-1z" />
                  </svg>

                  <button
                    className="font-semibold text-gray-700"
                    style={{ color: 'var(--text-color)' }}
                    onClick={() => handleClick('login')}
                  >
                    Login
                  </button>
                </div>

                  <span className="text-gray-700">|</span>

                  {/* Right Section: NFT Access */}
                  <div>
                    <NFTAccessSection />
                  </div>
                  
                </div>
              </>
              )}
              <span className="text-gray-700">|</span>



<button
  onClick={toggleSearch}
  className="pr-2 text-2xl dark:text-[#2F5E4D] hover:text-[#3D7D63] transition-colors"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}  
  >

    <circle cx="11" cy="10" r="6" strokeWidth={2} />  
    <line x1="15" y1="15" x2="21" y2="22" strokeWidth={2} /> 
  </svg>
</button>



           
          </>
        )}
      </div>

      {isSearchOpen && (
        <div style={{backgroundColor: 'var(--background-color)'}} className="w-full md:w-auto fixed md:relative top-0 left-0 h-full md:h-auto" ref={searchRef}>
          <form className="w-full max-w-sm" onSubmit={handleSearch}>
            <div style={{borderColor: 'var(--border-color)'}} className="flex items-center border-b-2 py-2">
              <input 
                style={{color: 'var(--text-color)'}} 
                className="appearance-none bg-transparent border-none w-full mr-3 py-1 px-2 leading-tight focus:outline-none"
                type="text"
                placeholder="Search"
                value={searchInput}
                onChange={handleInputChange}
                autoFocus
              />
            </div>
          </form>
        </div>
      )}
       </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div style={{backgroundColor: 'var(--background-color)'}} className="relative z-3 transform translate-x-0 transition-transform ease-in-out duration-500 hidden:md mobile-menu" ref={mobileMenuRef}>
          <div className="w-full h-full relative overflow-auto">
            <div className="flex flex-col p-4">

              {/* Theme Toggle and Network Selection */}
              <div className="flex justify-between items-center mb-4">
                {/* Theme Toggle - Aligned to the Left */}
                <div>
                  <ThemeToggle />
                </div>

                {/* Network Selection - Aligned to the Right */}

                <select
                  onChange={handleNetworkChange}
                  value={selectedNetwork}
                  className="text-black bg-white py-2"
                >
                  <option value="mainnet">Mainnet</option>
                  <option value="scroll">Scroll</option>
                </select>

              </div>
              <div className="flex justify-between items-center mb-4">
                {authContext.isAuthenticated ? (
                    <div className="relative inline-block text-left" ref={dropdownRef}>
                      <div>
                        <button type="button" aria-haspopup="true" aria-expanded={isDropdownOpen} className="inline-flex w-full py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          <span className="username-truncate"  style={{color: 'var(--text-color)'}} title={authContext.currentUser}>{authContext.currentUser.substring(0, 7)}</span>
                          <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>

                      {isDropdownOpen && (
                      
                          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                            <Link to="/account" style={{color: 'var(--text-color)'}} className="block pt-4 text-sm z-4" role="menuitem">Account</Link>
                            <a href="#" onClick={authContext.logout} style={{color: 'var(--text-color)'}} className="block pt-4 text-sm" role="menuitem">Logout</a>
                          </div>
                     
                      )}
                    </div>
                  ) : (
                  <>
                  <div className="flex items-center">
                  
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-4 mb-1 text-black dark:text-[#2F5E4D]"
                      fill="currentColor"
                      viewBox="0 0 24 23"
                      stroke="currentColor"
                      strokeWidth={0.5}
                    >
                      <path d="M12 2C9.2 2 7 4.2 7 7v3H6c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2h-1V7c0-2.8-2.2-5-5-5zm0 2c1.7 0 3 1.3 3 3v3H9V7c0-1.7 1.3-3 3-3zm-6 8h12v8H6v-8zm6 2c-.6 0-1 .4-1 1v2c0 .6.4 1 1 1s1-.4 1-1v-2c0-.6-.4-1-1-1z" />
                    </svg>

                    <button style={{color: 'var(--text-color)'}} className="font-semibold" onClick={() => {handleClick("login"); toggleMobileMenu();}}>Login</button>
                  </div>
                  </>
                )}
                    {/* NFT Access Button */}
                    <div className="ml-4">
                      <button style={{color: 'var(--text-color)'}} className="py-2  font-semibold" onClick={authContext.NFTaccess}>NFT Access</button>
                    </div>
              </div>
            <ul>

              {links.map(link => (
                <li 
                  key={link} 
                  style={{ color: 'var(--text-color)' }} 
                  className="list-item block text-[0.88rem] leading-[3.13rem] pr-4 relative"
                >
                  <Link 
                    to={`/news/${link.toLowerCase()}`} 
                    style={{ color: 'var(--text-color)' }} 
                    className="cursor-pointer text-[0.75rem] font-semibold relative text-center uppercase whitespace-nowrap" 
                    onClick={toggleMobileMenu}
                  >
                    {link}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Subscribe Button and Search Icon in One Line */}
            <div className="flex justify-between items-center">
              <Link to="/subscription" onClick={toggleMobileMenu}>
                <button 
                  style={{ color: 'var(--text-color)', paddingLeft: '1rem', paddingRight: '1.4rem', height: '1.7rem', marginTop:'1rem', paddingBottom: '0.5rem', paddingTop:'0.5rem' }} 
                  className="rounded-tl-3xl cursor-pointer inline-block text-[0.75rem] font-semibold leading-1 uppercase bg-button-bg">
                  Subscribe
                </button>
              </Link>

 

              <button 
                onClick={() => { toggleSearch(); toggleMobileMenu(); }} 
                className="pl-12 pt-2 text-2xl dark:text-[#2F5E4D] hover:text-[#3D7D63] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}  
                >
                  <circle cx="11" cy="10" r="6" strokeWidth={2} />  
                  <line x1="15" y1="15" x2="21" y2="22" strokeWidth={2} /> 
                </svg>
              </button>

            </div>
            </div>
          </div>
        </div>

      )}
     
    </nav>
  );
}

export default Navbar;